import consumer from "../.././channels/consumer"
import { nextStreamState } from "./stream_order"

// Inject the small amount of CSS that makes streaming feel smooth: a blinking
// "still generating" caret on the report, and a gentle fade-in for each newly
// appended block so nothing pops in harshly.
function ensureFeedbackStreamStyles() {
  if (document.getElementById('js-feedback-stream-styles')) return;
  const style = document.createElement('style');
  style.id = 'js-feedback-stream-styles';
  style.textContent = `
    #js-chat-feedback .js-feedback-caret::after {
      content: '';
      display: inline-block;
      width: 0.5em;
      height: 1.05em;
      margin-left: 2px;
      vertical-align: text-bottom;
      background: currentColor;
      opacity: 0.55;
      animation: jsFeedbackCaret 1s steps(2, start) infinite;
    }
    @keyframes jsFeedbackCaret { 0%,100% { opacity: 0; } 50% { opacity: 0.55; } }
    .js-feedback-fade-in { animation: jsFeedbackFadeIn 0.35s ease both; }
    @keyframes jsFeedbackFadeIn { from { opacity: 0; } to { opacity: 1; } }
    #js-chat-feedback.is-streaming {
      padding-bottom: 2rem;
    }
  `;
  document.head.appendChild(style);
}

// Reconcile the live DOM toward `next` in place (a tiny morphdom). Nodes that are
// already settled are left completely untouched — only the trailing in-progress
// text node grows and brand-new blocks get appended. This is what keeps the
// already-written feedback rock-steady and readable while more streams in, instead
// of re-parsing the whole report on every chunk.
function morph(live, next) {
  const nextChildren = Array.from(next.childNodes);

  for (let i = 0; i < nextChildren.length; i++) {
    const n = nextChildren[i];
    const l = live.childNodes[i];

    if (!l) {
      // brand-new node: append and fade it in if it's an element
      const clone = n.cloneNode(true);
      if (clone.nodeType === Node.ELEMENT_NODE) clone.classList.add('js-feedback-fade-in');
      live.appendChild(clone);
      continue;
    }

    if (l.nodeType !== n.nodeType || l.nodeName !== n.nodeName) {
      live.replaceChild(n.cloneNode(true), l);
      continue;
    }

    if (n.nodeType === Node.TEXT_NODE) {
      if (l.textContent !== n.textContent) l.textContent = n.textContent;
      continue;
    }

    if (n.nodeType === Node.ELEMENT_NODE) {
      morph(l, n); // recurse so only the changed leaf updates
    }
  }

  // drop any trailing live nodes the new render no longer has
  while (live.childNodes.length > nextChildren.length) {
    live.removeChild(live.lastChild);
  }
}

// Find the element the inline caret should sit on so its ::after renders right
// after the LAST streamed character. We descend by lastChild (through text nodes,
// not just elements) to the deepest trailing node: if it's a text node, the caret
// goes on its PARENT, so ::after lands after that trailing text. Descending by
// lastElementChild alone overshoots — it parks the caret after the last inline
// <b>/<del> while later trailing text keeps rendering past it (the caret looked
// stuck mid-line). Returns null if the deepest node is a bare text child of the
// container (transient, at stream start) so the caller skips gracefully.
function caretTarget(root) {
  // Put the caret right after the LAST non-empty text node, on its parent element,
  // so ::after lands at the end of the trailing text. Walking lastChild blindly can
  // land on a trailing <br> or a freshly-created empty element, whose ::after renders
  // as a block on its own line below the report — that's the "caret below the text" bug.
  const findLastText = (node) => {
    for (let i = node.childNodes.length - 1; i >= 0; i--) {
      const c = node.childNodes[i];
      if (c.nodeType === Node.TEXT_NODE) {
        if (c.textContent && c.textContent.trim().length) return c;
      } else if (c.nodeType === Node.ELEMENT_NODE) {
        const found = findLastText(c);
        if (found) return found;
      }
    }
    return null;
  };
  const textNode = findLastText(root);
  const el = textNode ? textNode.parentElement : null;
  return el && el !== root ? el : null;
}

document.addEventListener('turbolinks:load', function() {
  const chatFeedbackEl = document.getElementById('js-chat-feedback-container');

  if (chatFeedbackEl) {
    let chatId = chatFeedbackEl.dataset.chatId;
    let inactivityTimeout;
    let isStreaming = false;
    let caretEl = null; // element currently carrying the inline blinking caret

    // Check if feedback is already generated
    const feedbackGeneratedEl = document.getElementById('js-chat-feedback-success');
    const isFeedbackGenerated = !feedbackGeneratedEl.classList.contains('d-none');

    if (!isFeedbackGenerated) {
      const loadingEl = document.getElementById('js-chat-feedback-loading');
      const successEl = document.getElementById('js-chat-feedback-success');
      const reportEl = document.getElementById('js-chat-feedback');
      const scratch = document.createElement('div'); // offscreen parse target
      const RENDER_INTERVAL_MS = 500;
      let pendingHtml = null;
      let renderTimer = null;
      let streamState = { attempt: 0, length: 0 };

      ensureFeedbackStreamStyles();

      // Reload if nothing arrives / generation stalls. This is also the safety net
      // when the cable connection never establishes (e.g. an https page whose cable
      // URL is insecure ws://): without it the page would hang on the skeleton
      // forever. The worker still saves the report, so a reload renders it.
      const resetInactivityTimeout = () => {
        clearTimeout(inactivityTimeout);
        inactivityTimeout = setTimeout(() => {
          console.log("Feedback not received over cable. Refreshing page.");
          window.location.reload();
        }, 30000);
      };

      // First streamed chunk: swap the skeleton loader for the live report area.
      const beginStreaming = () => {
        if (isStreaming) return;
        isStreaming = true;
        if (loadingEl) loadingEl.classList.add('d-none');
        if (successEl) successEl.classList.remove('d-none');
        if (reportEl) reportEl.classList.add('is-streaming');
      };

      // Place the blinking caret inline, right after the last streamed text.
      // morph() never syncs classes on matching element nodes, so a manually
      // added class persists across morphs — that's why we must explicitly
      // remove it from the previously-marked element before re-marking the new
      // deepest-last element on every chunk.
      const updateCaret = () => {
        if (!reportEl) return;
        if (caretEl) {
          caretEl.classList.remove('js-feedback-caret');
          caretEl = null;
        }
        const target = caretTarget(reportEl);
        if (target) {
          target.classList.add('js-feedback-caret');
          caretEl = target;
        }
      };

      const clearCaret = () => {
        if (caretEl) {
          caretEl.classList.remove('js-feedback-caret');
          caretEl = null;
        }
      };

      const clearPendingRender = () => {
        clearTimeout(renderTimer);
        renderTimer = null;
        pendingHtml = null;
      };

      const flushPendingRender = () => {
        clearTimeout(renderTimer);
        renderTimer = null;
        if (!reportEl || pendingHtml === null) return;

        scratch.innerHTML = pendingHtml;
        pendingHtml = null;
        morph(reportEl, scratch);
        updateCaret();
      };

      const scheduleRender = (html, attempt = 1) => {
        const next = nextStreamState(streamState, html.length, attempt);
        if (!next) return;

        streamState = next;
        pendingHtml = html;
        if (renderTimer) return;

        renderTimer = setTimeout(flushPendingRender, RENDER_INTERVAL_MS);
      };

      const showFailureState = () => {
        clearPendingRender();
        clearCaret();
        if (reportEl) reportEl.classList.remove('is-streaming');

        const container = document.getElementById('js-chat-feedback-container');
        if (!container) {
          window.location.reload();
          return;
        }

        container.innerHTML = `
          <div class="text-center p-4">
            <div class="text-muted mb-3">We couldn’t generate your feedback this time.</div>
            <button type="button" class="btn btn-primary js-chat-feedback-retry">Try again</button>
          </div>
        `;

        const retryButton = container.querySelector('.js-chat-feedback-retry');
        if (retryButton) retryButton.addEventListener('click', () => window.location.reload());
      };

      // On completion we need the server-rendered final state: the authoritative
      // report plus the footer (Grammar Hub + PDF download links) and the success
      // header, none of which exist in the DOM during streaming. Instead of a full
      // page reload, fetch the same page and swap in just the feedback container —
      // this keeps the nav/sidebar and the reader's scroll position. Falls back to
      // a full reload if anything is unexpected.
      const finalizeReport = () => {
        fetch(window.location.href, { credentials: 'same-origin' })
          .then((res) => (res.ok ? res.text() : Promise.reject(new Error('status ' + res.status))))
          .then((html) => {
            const fresh = new DOMParser().parseFromString(html, 'text/html')
              .getElementById('js-chat-feedback-container');
            const current = document.getElementById('js-chat-feedback-container');
            const freshSuccess = fresh && fresh.querySelector('#js-chat-feedback-success');
            const freshReport = fresh && fresh.querySelector('#js-chat-feedback');
            const hasSavedFeedback = freshSuccess &&
              !freshSuccess.classList.contains('d-none') &&
              freshReport &&
              freshReport.textContent.trim().length > 0;

            if (fresh && current && hasSavedFeedback) {
              current.replaceWith(fresh);
            } else {
              showFailureState();
            }
          })
          .catch(showFailureState);
      };

      const subscription = consumer.subscriptions.create({ channel: "ChatFeedbackChannel", chat_id: chatId }, {
        connected() {
          resetInactivityTimeout();
        },
        received(data) {
          if (data.completed) {
            clearTimeout(inactivityTimeout);
            flushPendingRender();
            clearCaret();
            if (reportEl) reportEl.classList.remove('is-streaming');
            subscription.unsubscribe();
            if (data.failed) {
              showFailureState();
              return;
            }
            // Swap in the server-rendered final report container without a full reload.
            console.log("Feedback generation completed. Updating report.");
            finalizeReport();
            return;
          }

          if (data.type === 'feedback_delta' && typeof data.html === 'string' && data.html.length) {
            beginStreaming();
            resetInactivityTimeout();
            if (reportEl) {
              // Buffer fast-arriving cumulative HTML and morph the DOM at a calmer
              // cadence, so the report does not visually jitter on every SSE chunk.
              scheduleRender(data.html, Number(data.attempt) || 1);
            }
          }
        }
      });

      // Arm the safety-net immediately, not only inside connected(): if the socket
      // never connects, connected() never fires and the page would hang forever.
      resetInactivityTimeout();

      // Tear down on Turbolinks navigation. Returning a cleanup function from a
      // turbolinks:load handler does nothing (addEventListener ignores return
      // values) — that would leak the subscription and inactivity timeout and
      // create duplicate subscriptions on the next visit. before-cache fires when
      // navigating away, including mid-generation.
      document.addEventListener('turbolinks:before-cache', function teardown() {
        clearTimeout(inactivityTimeout);
        clearPendingRender();
        subscription.unsubscribe();
        document.removeEventListener('turbolinks:before-cache', teardown);
      });
    }
  }
});
